import React, { useEffect } from 'react';
import { useFrontendConfigs } from './hooks/useFrontendConfigs';
import { initThirdParty, syncGroundcoverPublicConfig, identifyUserflowUser } from './lib/frontendConfigs';
import { useLogoutAfterMaxPolls } from './hooks/useLogoutAfterMaxPolls';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import { useUser } from '@/hooks/user/useUser';
import { MaintenancePage } from '@/components/MaintenancePage';
import { GroundcoverIdentifyEffect } from '@/lib/groundcover/GroundcoverIdentifyEffect';
import { HeapIdentifyEffect } from '@/lib/heap/HeapIdentifyEffect';
import { ProductFruitsEffect } from '@/lib/productFruits/ProductFruitsEffect';

interface LoadThirdPartyProps {
    children: React.ReactNode;
}

/** Runs session poll and third-party init only when app is not in maintenance (hooks must run in a component that is only mounted when not maintaining). */
const AppWithSessionAndThirdParty = ({
    configs,
    children,
}: {
    configs: Record<string, string>;
    children: React.ReactNode;
}) => {
    const sessionPollInterval = Number(configs['sessionPollInterval']) || 60;
    const sessionMaxPolls = Number(configs['sessionMaxPolls']) || 8;
    const useSessionAccessCookie = configs['useSessionAccessCookie'] === 'true' || configs['useSessionAccessCookie'] === '1';

    const { user } = useUser();

    useLogoutAfterMaxPolls(sessionPollInterval, sessionMaxPolls, { skipPolling: useSessionAccessCookie });

    useEffect(() => {
        initThirdParty(configs);
    }, [configs]);

    useEffect(() => {
        if (user) {
            identifyUserflowUser(configs, user);
        }
    }, [user, configs]);

    return <>{children}</>;
};

export const LoadThirdPartyConfigs = ({ children }: LoadThirdPartyProps) => {
    const { configs } = useFrontendConfigs();
    const siteUnderMaintenance = configs['siteUnderMaintenance'] === 'true';

    useFeatureFlags();

    if (siteUnderMaintenance) {
        return (
            <MaintenancePage message={configs['siteUnderMaintenanceMessage']} />
        );
    }

    // Initialize Groundcover synchronously before children render so `sessionStorage.gcId` exists
    // for the first RTK Query requests (e.g. `useGetUserQuery`) in the same commit as `prepareHeaders`.
    if (configs['groundcoverApiKey']) {
        syncGroundcoverPublicConfig(configs);
    }

    return (
        <AppWithSessionAndThirdParty configs={configs}>
            <HeapIdentifyEffect />
            <GroundcoverIdentifyEffect />
            <ProductFruitsEffect />
            {children}
        </AppWithSessionAndThirdParty>
    );
};
